import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { spotIdFromPrettyPath } from '../../utils/prettyRoutes';
import { Map as MapIcon, Skull, Shuffle } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { CopyTextButton } from '../../components/world/CopyTextButton';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { coordLabel, formatSeconds, isPoint, locationCopyText, mapName, mapPath, monsterName, monsterPath, spotPath } from '../../utils/worldFormat';
import { alternatives, selectionNote } from '../../utils/worldQueries';
import type { Spawn, WorldData } from '../../types/world';
export function SpotDetail() {
  const {
    spotId,
    spotSlug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-2793" data-mp="27rsmqlvaq5dh">
      {w => {
      const id = spotSlug ? spotIdFromPrettyPath(pathname) : spotId;
      const s = id ? w.spawnById.get(id) : undefined;
      if (!s) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-2794" data-mp="2gsgz7c51jxo9">
              <p className="text-white" data-id="element-2795" data-mp="2gs7b5u3k40l7">No encontramos ese registro de aparición.</p>
              <Link to="/guia/mapas?vista=apariciones" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-2796" data-mp="2lpzctfu0712l">Volver a Apariciones</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={spotPath(s.id)} data-id="element-2797" data-mp="2vgk56lj339d2" />
            <SpotView w={w} s={s} data-id="element-2798" data-mp="2zjgt8rre7xoe" />
          </>;
    }}
    </WorldGate>;
}
function SpotView({
  w,
  s
}: {
  w: WorldData;
  s: Spawn;
}) {
  const mon = monsterName(w, s.monsterId);
  const map = mapName(w, s.mapId);
  const catalog = w.monsters.get(s.monsterId);
  const coords = coordLabel(s);
  const alts = s.mapSelection === 'random' ? alternatives(w, s) : [];
  useEffect(() => {
    document.title = `${mon} en ${map} · Guía MU Vanguard`;
  }, [mon, map]);
  const facts: {
    label: string;
    value: string;
  }[] = [{
    label: 'Tipo de aparición',
    value: s.kind === 'invasion' ? 'Invasión' : 'Spot'
  }, ...(s.quantity !== undefined ? [{
    label: 'Cantidad configurada',
    value: String(s.quantity)
  }] : []), {
    label: isPoint(s) ? 'Coordenada' : 'Área de aparición',
    value: coords ?? 'Coordenadas no documentadas'
  }, ...(s.respawnSeconds !== undefined ? [{
    label: 'Reaparición configurada',
    value: formatSeconds(s.respawnSeconds)
  }] : []), ...(s.mapSelection ? [{
    label: 'Selección de mapa',
    value: s.mapSelection === 'random' ? 'Aleatoria' : 'Fija'
  }] : [])];

  /** Estadística efectiva del spot; si falta, se usa el catálogo y se indica. */
  const stat = (label: string, eff?: number, cat?: number) => eff !== undefined ? {
    label,
    value: eff
  } : {
    label,
    value: cat,
    note: cat !== undefined ? '(dato de catálogo)' : undefined
  };
  return <div data-id="element-2799" data-mp="2efwghq0r882i">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: map,
      to: mapPath(s.mapId)
    }, {
      label: mon
    }]} eyebrow={s.kind === 'invasion' ? 'Aparición de invasión' : 'Spot'} title={`${mon} · ${map}`} data-id="element-2800" data-mp="20mdgq0nunddr" />

      <dl className="grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2" data-id="element-2801" data-mp="1qqk5qlps3ry3">
        {facts.map(f => <div key={f.label} className="bg-[#0A1628] px-5 py-4" data-id="element-2802" data-mp="1hoau1sirxsj4">
            <dt className="text-xs text-gray-500" data-id="element-2803" data-mp="1lp4nt2iwe1jx">{f.label}</dt>
            <dd className={`mt-1 text-lg text-white ${f.label.startsWith('Coord') || f.label.startsWith('Área') ? 'font-mono' : 'font-semibold'}`} data-id="element-2804" data-mp="1lpedep0166p3">{f.value}</dd>
          </div>)}
      </dl>

      {coords && !isPoint(s) && <p className="mt-3 text-sm text-gray-500" data-id="element-2805" data-mp="3g0t2r4p4q44b">Es un área: el monstruo puede aparecer en cualquier punto dentro de esos límites.</p>}

      <div className="mt-6 flex flex-wrap gap-2.5" data-id="element-2806" data-mp="2md53heqwoipa">
        <Link to={monsterPath(s.monsterId)} className="inline-flex h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-2807" data-mp="2mbbu3rowrkdl">
          <Skull className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2808" data-mp="28dvo2haixsmd" />
          Ver monstruo
        </Link>
        <Link to={mapPath(s.mapId)} className="inline-flex h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-2809" data-mp="23lvst25efnw4">
          <MapIcon className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2810" data-mp="1tmmqnm0dr2yo" />
          Ver mapa
        </Link>
        {coords && <CopyTextButton text={locationCopyText(map, mon, s)} label="Copiar ubicación" copiedLabel="Ubicación copiada" data-id="element-2811" data-mp="1ko1c01y14836" />}
        <CopyLinkButton data-id="element-2812" data-mp="6jr0lxffz80" />
      </div>

      <div className="mt-10 space-y-10" data-id="element-2813" data-mp="5h2gknku8v9">
        {(s.eventId || s.mapSelection === 'random') && <WorldSection id="invasion" title="Invasión" data-id="element-2814" data-mp="460x9hkoh0e8">
            {s.eventId && <EventChip w={w} eventId={s.eventId} data-id="element-2815" data-mp="96f9lbtbawi2" />}
            {selectionNote(s) && <p className="mt-4 flex items-start gap-2 text-gray-200" data-id="element-2816" data-mp="e6bb94ejebug">
                <Shuffle className="mt-1 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-2817" data-mp="e3fc5cqydh4f" />
                {selectionNote(s)}
              </p>}
            {s.possibleMaps.length > 0 && <p className="mt-3 text-sm text-gray-400" data-id="element-2818" data-mp="3mllbs82s3904">Mapas posibles: {s.possibleMaps.map(id => mapName(w, id)).join(', ')}.</p>}
            {alts.length > 1 && <div className="mt-4" data-id="element-2819" data-mp="3reecmkv4lx6x">
                <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2820" data-mp="2txfqnk7tbhzr">Registros alternativos de esta misma aparición</h3>
                <ul className="flex flex-wrap gap-2" data-id="element-2821" data-mp="2yupmpippirwk">
                  {alts.map(a => <li key={a.id} data-id="element-2822" data-mp="2qu9lwdvxh8xe">
                      <Link to={spotPath(a.id)} aria-current={a.id === s.id ? 'page' : undefined} className={`inline-flex h-10 items-center rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${a.id === s.id ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-200 hover:text-white'}`} data-id="element-2823" data-mp="2qt865w3yvy9c">
                        {mapName(w, a.mapId)} · {coordLabel(a) ?? 'sin coordenadas'}
                      </Link>
                    </li>)}
                </ul>
                <p className="mt-2 text-xs text-gray-500" data-id="element-2824" data-mp="2hsjtb4qb83y1">No se suman: es un solo grupo de {s.quantity ?? '?'} que aparece en uno de estos mapas.</p>
              </div>}
            <p className="mt-4 text-sm text-gray-500" data-id="element-2825" data-mp="3t88fvuznj0yb">La reaparición de un spot no es la frecuencia de la invasión. Los horarios se consultan en el juego con la tecla H.</p>
          </WorldSection>}

        <Disclosure title="Estadísticas de esta aparición" data-id="element-2826" data-mp="1a1mj9eze8pcp">
          <StatList rows={[stat('Vida', s.hp, catalog?.hp), stat('Daño mínimo', s.damageMin, catalog?.damageMin), stat('Daño máximo', s.damageMax, catalog?.damageMax), stat('Defensa', s.defense, catalog?.defense), {
          label: 'Nivel del monstruo (catálogo)',
          value: catalog?.level
        }, {
          label: 'Registro',
          value: s.id
        }]} data-id="element-2827" data-mp="1f2ak0sdfckk4" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-2828" data-mp="1xrqkxgt0uf12">Se muestran los valores configurados para esta aparición. Los marcados como catálogo no tienen valor propio en spawns.csv.</p>
        </Disclosure>
      </div>
    </div>;
}